 
import React from "react";
import {
  Box,
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import { useFormik } from "formik";

import {
  useDispatchWrapper,
  usePoliciesByCustomer,
} from "../../../hooks";
import { LoadState, MODULE_IDS } from "../../../constants/enums";
import {
  ControlledCustomSelect,
  CustomCheckBoxWithLabel,
  CustomFormLabel,
  CustomTextField,
} from "../../../components/formsComponents";
import { StandadCard } from "../../../components/Cards";
import { CustomDatePicker } from "../../../components/formsComponents/CustomDatePicker";
import { RoleBasedCustomButton } from "../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import {
  select_vehicle_coverage_dropdown,
} from "../../../constants/constants";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../redux/initialStoreState";
import { PageContainer } from "../../../components/container/PageContainer";
import { PageLoader } from "../../../components/PageLoader/PageLoader";
import { IBreadcrumbProps } from "../../../layout/fullLayout/breadcrumb/IBreadcrumbProps";
import { Breadcrumb } from "../../../layout/fullLayout/breadcrumb/Breadcrumb";
import {
  clearMpoiListState,
  fetchMpoiAsync,
  upsertMpoiAsync,
} from "../../../redux/MPOI/MpoiActions";
import { LabelCard } from "../../../components/LabelCard/LabelCard";
import { fetchAutomobileCustomerInfoAsync } from "../../../redux/automobileBasicDetails/automobileBasicDetailsActions";
import { AddressSearchAutoComplete } from "../../../components/AddressAutoComplete/AddressSearchAutoComplete";
import { ILocationResponsePayload } from "../../../components/LocationAutoComplete/LocationAutoComplete";
import produce from "immer";
import {
  getBranchDataByCode,
  getInsurerDataByCode,
} from "../../../helpers/getNameByCode";
import { Delete } from "@mui/icons-material";
import { getUniqueId } from "../../../helpers/getUniqueId";

const CANADIAN_PROVINCE_MAP: Record<string, string> = {
  alberta: "AB",
  "british columbia": "BC",
  manitoba: "MB",
  "new brunswick": "NB",
  "newfoundland and labrador": "NL",
  newfoundland: "NL",
  labrador: "NL",
  "northwest territories": "NT",
  "nova scotia": "NS",
  nunavut: "NU",
  ontario: "ON",
  "prince edward island": "PE",
  quebec: "QC",
  saskatchewan: "SK",
  yukon: "YT",
};

/**
 * Converts a full Canadian province name to its two-letter code.
 * If the value is already a code or unrecognized, it is returned as-is.
 */
const toProvinceCode = (value: string | undefined | null): string => {
  if (!value) return "";
  const trimmed = value.trim();
  // Already a 2-letter code
  if (trimmed.length === 2) return trimmed.toUpperCase();
  return CANADIAN_PROVINCE_MAP[trimmed.toLowerCase()] ?? trimmed;
};

export interface IVehicleDropdownOption {
  label: string;
  value: string;
}

export interface MpoiProps {
  onSuccess: () => void;
  breadcrumbItems?: IBreadcrumbProps["items"];
  customerId?: string;
  customerPolicyId?: string;
  requestUuid?: string;
  vehicleList?: any[];
  vehcileDropdown?: IVehicleDropdownOption[];
  policyType?: string;
}

export const Mpoi: React.FC<MpoiProps> = ({
  onSuccess,
  breadcrumbItems,
  customerId,
  customerPolicyId,
  requestUuid,
  vehicleList,
  vehcileDropdown,
  policyType,
}) => {
  const { mpoi, mpoiLoading, error } = useSelector(
    (state: IStoreState) => state.mpoi
  );
  const { fetchPoliciesByCustomerPolicyId } = usePoliciesByCustomer();
  const personalInfo = useSelector(
    (state: IStoreState) => state.customer.personalInformation.data
  );

  const [saveLoading, setSaveLoading] = React.useState(false);
  const dispatch = useDispatchWrapper();

  // State for current vehicle input
  const [currentVehicle, setCurrentVehicle] = React.useState({
    year: "",
    make: "",
    model: "",
    vin: "",
    description: "",
    short_description: "",
  });

  const [vehicleInfo, setVehicleInfo] = React.useState<string>("");

  // State to track which vehicle is being edited
  const [editingVehicleKey, setEditingVehicleKey] = React.useState<
    string | null
  >(null);

  // State for current email input
  const [currentEmail, setCurrentEmail] = React.useState("");
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const isCurrentEmailValid =
    currentEmail.trim().length > 0 && emailRegex.test(currentEmail.trim());

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: {
      ...mpoi,
      customer_id: customerId as any,
      customer_policy_id: customerPolicyId as any,
    },
    validate: (values) => {
      const errors: any = {};

      const emails = values.customer_email_address || [];
      // Max count check
      if (emails.length > 25) {
        errors.customer_email_address =
          "You can only add up to 25 email addresses";
        return errors;
      }

      // Loop each email
      for (const email of emails) {
        if (!emailRegex.test(email)) {
          errors.customer_email_address = "Email address is invalid";
          break;
        }
      }

      return errors;
    },

    onSubmit: (values) => {
      if(!customerId || !customerPolicyId){
        return;
      }
      setSaveLoading(true);
      dispatch(
        upsertMpoiAsync({ ...values, customer_id: customerId as any, customer_policy_id: customerPolicyId as any  }, (isSuccess) => {
          if (isSuccess) {
            onSuccess();
          }
          setSaveLoading(false);
        })
      );
    },
  });

  const handleAddress = (data: ILocationResponsePayload) => {
    setValues((prevValues: any) => ({
      ...prevValues,
      customer_address: {
        address_one: data.address,
        city: data.city,
        state_prov_cd: toProvinceCode(data.state),
        postal_code: data.postalCode,
      },
    }));
  };

  const handleInsurer = async (code: string) => {
    const insurer = await getInsurerDataByCode(code);
    if (insurer) {
      const newValues = produce(values, (draftValues) => {
        draftValues.insurer_code = insurer.insurer_code;
        draftValues.insurance_company = insurer.name;
        draftValues.insurance_company_address = {
          address: insurer.address,
          city: insurer.city,
          state_prov_cd: toProvinceCode(insurer.province_or_state),
          postal_code: insurer.postal_code,
        };
      });
      setValues(newValues);
    }
  };

  const handleBranch = async (code: string) => {
    const branch = await getBranchDataByCode(code);
    if (branch) {
      const newValues = produce(values, (draftValues) => {
        draftValues.branch_code = branch.branch_code;
        draftValues.branch_name = branch.name;
        draftValues.branch_address = {
          address_one: branch.address,
          city: branch.city,
          state_prov_cd: toProvinceCode(branch.province_or_state),
          postal_code: branch.postal_code,
        };
      });
      setValues(newValues);
    }
  };

  const formatCanadianPhone = (phone: string = "") => {
    const cleaned = phone.replace(/\D/g, "");

    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return `+1-${cleaned.slice(1, 4)}-${cleaned.slice(4)}`;
    }

    if (cleaned.length === 10) {
      return `+1-${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    }

    return phone;
  };

  React.useEffect(() => {
    if (customerPolicyId) {
      dispatch(fetchAutomobileCustomerInfoAsync(Number(customerPolicyId)));
    }
  }, [customerPolicyId]);

  React.useEffect(() => {
    if (requestUuid) {
      dispatch(fetchMpoiAsync(requestUuid));
    }
  }, [requestUuid]);

  React.useEffect(() => {
    if (!requestUuid) {
      const fetchData = async () => {
        if (customerPolicyId) {
          const policies = await fetchPoliciesByCustomerPolicyId(
            Number(customerPolicyId)
          );
          if (policies && policies.length > 0) {
            const policy = policies[0];
            setValues((prevValues: any) => ({
              ...prevValues,
              customer_name: policy.customer_name,
              customer_address: {
                address_one: policy.address,
                address_two: policy.unit_or_suite,
                city: policy.city,
                state_prov_cd: toProvinceCode(policy.province_or_state),
                postal_code: policy.postal_code,
              },
              line_of_business: policyType,
              // customer_email: policy.email,
              policy_number: policy.policy_number,
              policy_effective_date: policy.effective_date,
              policy_expiry_date: policy.expiry_date,
              insurer_code: policy.insurer,
              insurance_company: policy.name,
              customer_phone_number: formatCanadianPhone(personalInfo?.phone),
              customer_email_address: personalInfo?.email
                ? [personalInfo?.email]
                : [policy.email],
              // insurance_company_address: {
              //   address: insurer.address,
              //   city: insurer.city,
              //   state_prov_cd: insurer.province_or_state,
              //   postal_code: insurer.postal_code,
              // },
              branch_code: policy.branch_code,
              //branch_name: branch.name,
              // branch_address: {
              //   address_one: branch.address,
              //   city: branch.city,
              //   state_prov_cd: branch.province_or_state,
              //   postal_code: branch.postal_code,
              // },
              email_salutations: `Dear ${policy.customer_name},`,
            }));
          }
        }
      };
      fetchData();
    } else {
      setValues(mpoi);
    }
  }, [customerPolicyId]);

  React.useEffect(() => {
    if( mpoi ) {
      setValues(mpoi);
    }
  }, [mpoi]);

  const finalPageTitle = mpoi.customer_policy_id
    ? "Update My Proof of Insurance"
    : "Create My Proof of Insurance";

  const handleAddAutoVehicle = () => {
    const currentVehicleCount = (values.vehicle || []).length;
    if (
      currentVehicle.year &&
      currentVehicle.make &&
      currentVehicle.model &&
      currentVehicle.vin
    ) {
      if (editingVehicleKey) {
        const updatedVehicles = (values.vehicle || []).map((v) =>
          v.key === editingVehicleKey
            ? {
                ...v,
                year: Number(currentVehicle.year),
                make: currentVehicle.make,
                model: currentVehicle.model,
                vin: currentVehicle.vin,
              }
            : v
        );
        setFieldValue("vehicle", updatedVehicles);
        setEditingVehicleKey(null);
        setCurrentVehicle({
          year: "",
          make: "",
          model: "",
          vin: "",
          description: "",
          short_description: "",
        });
      } else if (currentVehicleCount < 25) {
        const newVehicle = {
          key: getUniqueId(),
          year: Number(currentVehicle.year),
          make: currentVehicle.make,
          model: currentVehicle.model,
          vin: currentVehicle.vin,
        };
        const updatedVehicles = [...(values.vehicle || []), newVehicle];
        setFieldValue("vehicle", updatedVehicles);
        setCurrentVehicle({
          year: "",
          make: "",
          model: "",
          vin: "",
          description: "",
          short_description: "",
        });
      }
    }
  };

  const handleAddFleetVehicle = () => {
    const currentVehicleCount = (values.vehicle || []).length;
    if (currentVehicle.description && currentVehicle.short_description) {
      if (editingVehicleKey) {
        // Update existing vehicle
        const updatedVehicles = (values.vehicle || []).map((v) =>
          v.key === editingVehicleKey
            ? {
                ...v,
                description: currentVehicle.description,
                short_description: currentVehicle.short_description,
              }
            : v
        );
        setFieldValue("vehicle", updatedVehicles);
        setEditingVehicleKey(null);
        setCurrentVehicle({
          year: "",
          make: "",
          model: "",
          vin: "",
          description: "",
          short_description: "",
        });
      } else if (currentVehicleCount < 25) {
        // Add new vehicle
        const newVehicle = {
          key: getUniqueId(),
          description: currentVehicle.description,
          short_description: currentVehicle.short_description,
        };
        const updatedVehicles = [...(values.vehicle || []), newVehicle];
        setFieldValue("vehicle", updatedVehicles);
        setCurrentVehicle({
          year: "",
          make: "",
          model: "",
          vin: "",
          description: "",
          short_description: "",
        });
      }
    }
  };

  const handleVehicleInfoChange = (value: string) => {
    if (!value || !vehicleList) return;

    // Find vehicle by incremental_number (which is the value in the dropdown)
    // Convert to string for comparison since dropdown value is always string
    const vehicleInfo = vehicleList.find(
      (v: any) => String(v.incremental_number) === value
    );
    if (vehicleInfo) {
      const addNewVehicle = {
        key: getUniqueId(),
        year: vehicleInfo.year,
        make: vehicleInfo.make,
        model: vehicleInfo.model,
        vin: vehicleInfo.vin,
      };
      const updatedVehicles = [...(values.vehicle || []), addNewVehicle];
      setFieldValue("vehicle", updatedVehicles);
      setVehicleInfo(""); // Reset the dropdown selection
      setCurrentVehicle({
        year: "",
        make: "",
        model: "",
        vin: "",
        description: "",
        short_description: "",
      });
    } else {
      // If vehicle not found, reset the selection
      setVehicleInfo("");
    }
  };

  const handleAddEmail = () => {
    if (currentEmail.trim()) {
      const emailArray = values.customer_email_address || [];
      if (!emailArray.includes(currentEmail.trim())) {
        setFieldValue("customer_email_address", [
          ...emailArray,
          currentEmail.trim(),
        ]);
        setCurrentEmail("");
      }
    }
  };

  const handleDeleteEmail = (index: number) => {
    const emailArray = values.customer_email_address || [];
    const updatedEmails = emailArray.filter((_, i) => i !== index);
    setFieldValue("customer_email_address", updatedEmails);
  };

  React.useEffect(() => {
    return () => {
      dispatch(clearMpoiListState());
    };
  }, [dispatch]);

  return (
    <PageContainer title={finalPageTitle} description="this is innerpage">
      <PageLoader
        loading={mpoiLoading === LoadState.InProgress}
        error={error ? { message: error } : null}
      >
        {!customerId && !customerPolicyId && (
          <Breadcrumb title="" items={breadcrumbItems} />
        )}
        <StandadCard sx={{ mt: 2 }} heading={finalPageTitle}>
          <form onSubmit={handleSubmit}>
            <LabelCard heading="Name and Contact Information" />
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}>
                <CustomFormLabel>Customer Name</CustomFormLabel>
                <CustomTextField
                  name="customer_name"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.customer_name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <AddressSearchAutoComplete
                  label="Address 1"
                  address={values.customer_address.address_one}
                  onAddressChange={handleAddress}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomFormLabel>Address 2</CustomFormLabel>
                <CustomTextField
                  name="customer_address.address_two"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.customer_address.address_two}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>City</CustomFormLabel>
                <CustomTextField
                  name="customer_address.city"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.customer_address.city}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Province</CustomFormLabel>
                <CustomTextField
                  name="customer_address.state_prov_cd"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.customer_address.state_prov_cd}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Postal Code</CustomFormLabel>
                <CustomTextField
                  name="customer_address.postal_code"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.customer_address.postal_code}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <CustomFormLabel>Line of Business</CustomFormLabel>
                <ControlledCustomSelect
                  variant="outlined"
                  size="small"
                  type="string"
                  name="line_of_business"
                  fullWidth
                  value={values.line_of_business}
                  onChange={handleChange}
                  displayEmpty
                  placeholder={"Select one"}
                  options={[
                    { label: "Automobile", value: "csio:AUTO" },
                    { label: "Commercial Auto", value: "csio:CAUTO" },
                  ]}
                  helperText={errors.line_of_business as string}
                ></ControlledCustomSelect>
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Language</CustomFormLabel>
                <ControlledCustomSelect
                  fullWidth
                  value={values.extra_fields.language_preferred}
                  name="extra_fields.language_preferred"
                  onChange={handleChange}
                  placeholder="Select One"
                  displayEmpty
                  options={["E", "F"].map((template) => {
                    return { label: template, value: template };
                  })}
                ></ControlledCustomSelect>
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Phone Number</CustomFormLabel>
                <CustomTextField
                  name="customer_phone_number"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.customer_phone_number}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Policy Status</CustomFormLabel>
                <ControlledCustomSelect
                  fullWidth
                  value={values.policy_status}
                  name="policy_status"
                  onChange={handleChange}
                  placeholder="Select One"
                  displayEmpty
                  options={[
                    { label: "New Business", value: "csio:NBS" },
                    { label: "Renewal", value: "csio:RWL" },
                  ]}
                ></ControlledCustomSelect>
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Email Address</CustomFormLabel>
                <Stack direction="row" spacing={1}>
                  <CustomTextField
                    variant="outlined"
                    size="small"
                    type="email"
                    fullWidth
                    value={currentEmail}
                    onChange={(e) => setCurrentEmail(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddEmail();
                      }
                    }}
                    error={errors.customer_email_address ? true : false}
                    helperText={errors.customer_email_address as string}
                    placeholder="Enter email address"
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddEmail}
                    disabled={!isCurrentEmailValid}
                    sx={{ minWidth: "auto", width: "300px", px: 2 }}
                  >
                    Add Recipient ({values.customer_email_address?.length || 0}
                    /25)
                  </Button>
                </Stack>
                {values.customer_email_address &&
                  values.customer_email_address.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  background: "#efefef",
                                  fontWeight: "bold",
                                }}
                              >
                                Email Address
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  background: "#efefef",
                                  fontWeight: "bold",
                                  width: 80,
                                }}
                              >
                                Action
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {values.customer_email_address.map(
                              (email, index) => (
                                <TableRow key={index}>
                                  <TableCell>{email}</TableCell>
                                  <TableCell align="center">
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() => handleDeleteEmail(index)}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
              </Grid>
            </Grid>
            <LabelCard heading="Policy Information" />
            <Grid container spacing={1}>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Policy Number</CustomFormLabel>
                <CustomTextField
                  name="policy_number"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.policy_number}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Policy Effective Date</CustomFormLabel>
                <CustomDatePicker
                  value={values.policy_effective_date || ""}
                  onChange={(value) =>
                    setFieldValue("policy_effective_date", value)
                  }
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomFormLabel>Policy Expiration Date</CustomFormLabel>
                <CustomDatePicker
                  value={values.policy_expiry_date || ""}
                  onChange={(value) =>
                    setFieldValue("policy_expiry_date", value)
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Endorsement Date</CustomFormLabel>
                <CustomDatePicker
                  value={values.extra_fields.endorsement_date || ""}
                  onChange={(value) =>
                    setFieldValue("extra_fields.endorsement_date", value)
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} lg={2}>
                <Box>
                  <Stack direction="row" justifyContent="space-between">
                    <CustomFormLabel>Brokerage Code</CustomFormLabel>
                    <Button
                      variant="text"
                      onClick={() => handleBranch(values.branch_code ?? "")}
                    >
                      Search Brokerage
                    </Button>
                  </Stack>
                  {/* <CustomFormLabel>Name</CustomFormLabel> */}
                  <CustomTextField
                    name="branch_code"
                    value={values.branch_code}
                    fullWidth
                    onChange={handleChange}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Brokerage</CustomFormLabel>
                <CustomTextField
                  name="branch_name"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.branch_name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Brokerage Address</CustomFormLabel>
                <CustomTextField
                  name="branch_address.address_one"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.branch_address?.address_one || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Brokerage City</CustomFormLabel>
                <CustomTextField
                  name="branch_address.city"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.branch_address?.city || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Brokerage Province</CustomFormLabel>
                <CustomTextField
                  name="branch_address.state_prov_cd"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.branch_address?.state_prov_cd || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Brokerage Postal Code</CustomFormLabel>
                <CustomTextField
                  name="branch_address.postal_code"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.branch_address?.postal_code || ""}
                  onChange={handleChange}
                />
              </Grid>

              {/* <Grid item xs={12} md={3}>
                  <InsurerCodeV2AutoSearch
                    value={values.insurer_code}
                    label="Insurer"
                    onSelect={handleInsurer}
                  />
                </Grid> */}
              <Grid item xs={12} lg={2}>
                <Box>
                  <Stack direction="row" justifyContent="space-between">
                    <CustomFormLabel>Insurer Code</CustomFormLabel>
                    <Button
                      variant="text"
                      onClick={() => handleInsurer(values.insurer_code ?? "")}
                    >
                      Search Insurer
                    </Button>
                  </Stack>
                  <CustomTextField
                    name="insurer_code"
                    value={values.insurer_code}
                    fullWidth
                    onChange={handleChange}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={2}>
                <CustomFormLabel>Insurer Name</CustomFormLabel>
                <CustomTextField
                  name="insurance_company"
                  value={values.insurance_company}
                  fullWidth
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} lg={2}>
                <CustomFormLabel>Insurer Address</CustomFormLabel>
                <CustomTextField
                  name="insurance_company_address.address"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.insurance_company_address?.address || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Insurer City</CustomFormLabel>
                <CustomTextField
                  name="insurance_company_address.city"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.insurance_company_address?.city || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Insurer Province</CustomFormLabel>
                <CustomTextField
                  name="insurance_company_address.state_prov_cd"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.insurance_company_address?.state_prov_cd || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomFormLabel>Insurer Postal Code</CustomFormLabel>
                <CustomTextField
                  name="insurance_company_address.postal_code"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.insurance_company_address?.postal_code || ""}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <LabelCard heading="Vehicle Information" />
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Stack direction={"row"} spacing={2} mt={3}>
                  <CustomCheckBoxWithLabel
                    label="Auto"
                    checked={
                      values.extra_fields.vehicle_information_type === "Auto"
                    }
                    onChange={(e, checked) => {
                      setFieldValue(
                        "extra_fields.vehicle_information_type",
                        checked ? "Auto" : ""
                      );
                      if (!checked) {
                        setCurrentVehicle({
                          year: "",
                          make: "",
                          model: "",
                          vin: "",
                          description: "",
                          short_description: "",
                        });
                        setEditingVehicleKey(null);
                      }
                    }}
                  />
                  <CustomCheckBoxWithLabel
                    label="Fleet"
                    checked={
                      values.extra_fields.vehicle_information_type === "Fleet"
                    }
                    onChange={(e, checked) => {
                      setFieldValue(
                        "extra_fields.vehicle_information_type",
                        checked ? "Fleet" : ""
                      );
                      if (!checked) {
                        setCurrentVehicle({
                          year: "",
                          make: "",
                          model: "",
                          vin: "",
                          description: "",
                          short_description: "",
                        });
                        setEditingVehicleKey(null);
                      }
                    }}
                  />
                </Stack>
              </Grid>
              {values.extra_fields.vehicle_information_type === "Auto" && (
                <>
                  {/* <Grid item xs={12} md={3}>
                    <CustomFormLabel>Year</CustomFormLabel>
                    <CustomTextField
                      variant="outlined"
                      size="small"
                      type="number"
                      fullWidth
                      value={currentVehicle.year}
                      onChange={(e) =>
                        setCurrentVehicle({
                          ...currentVehicle,
                          year: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <CustomFormLabel>Make</CustomFormLabel>
                    <CustomTextField
                      variant="outlined"
                      size="small"
                      type="text"
                      fullWidth
                      value={currentVehicle.make}
                      onChange={(e) =>
                        setCurrentVehicle({
                          ...currentVehicle,
                          make: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <CustomFormLabel>Model</CustomFormLabel>
                    <CustomTextField
                      variant="outlined"
                      size="small"
                      type="text"
                      fullWidth
                      value={currentVehicle.model}
                      onChange={(e) =>
                        setCurrentVehicle({
                          ...currentVehicle,
                          model: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <CustomFormLabel>VIN Number</CustomFormLabel>
                    <CustomTextField
                      variant="outlined"
                      size="small"
                      type="text"
                      fullWidth
                      value={currentVehicle.vin}
                      onChange={(e) =>
                        setCurrentVehicle({
                          ...currentVehicle,
                          vin: e.target.value,
                        })
                      }
                    />
                  </Grid> */}
                  <Grid item xs={12} md={3}>
                    <CustomFormLabel>Vehicle Information</CustomFormLabel>
                    <ControlledCustomSelect
                      variant="outlined"
                      size="small"
                      type="text"
                      fullWidth
                      displayEmpty
                      placeholder="Select One"
                      value={vehicleInfo}
                      options={vehcileDropdown || []}
                      onChange={(e) => {
                        const value = e.target.value as string;
                        setVehicleInfo(value);
                        handleVehicleInfoChange(value);
                      }}
                    />
                  </Grid>
                  {/* <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                  >
                    {editingVehicleKey && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => {
                          setEditingVehicleKey(null);
                          setCurrentVehicle({
                            year: "",
                            make: "",
                            model: "",
                            vin: "",
                            description: "",
                            short_description: "",
                          });
                        }}
                        sx={{ mt: 2 }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      disabled={
                        (values.vehicle || []).length >= 25 &&
                        !editingVehicleKey
                      }
                      onClick={() => handleAddAutoVehicle()}
                      sx={{ mt: 2 }}
                    >
                      {editingVehicleKey
                        ? "Update Vehicle"
                        : `Add Vehicle (${(values.vehicle || []).length}/25)`}
                    </Button>
                  </Grid> */}
                </>
              )}
              {values.extra_fields.vehicle_information_type === "Fleet" && (
                <>
                  <Grid item xs={12} md={3}>
                    <CustomFormLabel>Description</CustomFormLabel>
                    <ControlledCustomSelect
                      variant="outlined"
                      size="small"
                      type="text"
                      fullWidth
                      displayEmpty
                      placeholder="Select One"
                      value={currentVehicle.description}
                      options={select_vehicle_coverage_dropdown
                        .slice(1)
                        .map((STab) => {
                          return { label: STab, value: STab };
                        })}
                      onChange={(e) =>
                        setCurrentVehicle({
                          ...currentVehicle,
                          description: e.target.value as string,
                        })
                      }
                    />
                    {/* <CustomTextField
                      variant="outlined"
                      size="small"
                      type="text"
                      fullWidth
                      value={currentVehicle.description}
                      onChange={(e) =>
                        setCurrentVehicle({
                          ...currentVehicle,
                          description: e.target.value,
                        })
                      }
                    /> */}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <CustomFormLabel>Short Description</CustomFormLabel>
                    <CustomTextField
                      variant="outlined"
                      size="small"
                      type="text"
                      fullWidth
                      value={currentVehicle.short_description}
                      onChange={(e) =>
                        setCurrentVehicle({
                          ...currentVehicle,
                          short_description: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                  >
                    {editingVehicleKey && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => {
                          setEditingVehicleKey(null);
                          setCurrentVehicle({
                            year: "",
                            make: "",
                            model: "",
                            vin: "",
                            description: "",
                            short_description: "",
                          });
                        }}
                        sx={{ mt: 2 }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      disabled={
                        (values.vehicle || []).length >= 25 &&
                        !editingVehicleKey
                      }
                      onClick={() => handleAddFleetVehicle()}
                      sx={{ mt: 2 }}
                    >
                      {editingVehicleKey
                        ? "Update Vehicle"
                        : `Add Vehicle (${(values.vehicle || []).length}/25)`}
                    </Button>
                  </Grid>
                </>
              )}
              {values.vehicle && values.vehicle.length > 0 && (
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="vehicle table">
                      <TableHead sx={{ background: "#efefef" }}>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>VIN Number</TableCell>
                          <TableCell>Vehicle Information</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values.vehicle.map((vehicle, index) => {
                          // Determine vehicle information display based on available data
                          let vehicleInfo = "-";
                          if (vehicle.year || vehicle.make || vehicle.model) {
                            // Auto vehicle
                            vehicleInfo =
                              [vehicle.year, vehicle.make, vehicle.model]
                                .filter(Boolean)
                                .join(", ") || "-";
                          } else if (
                            vehicle.description ||
                            vehicle.short_description
                          ) {
                            // Fleet vehicle
                            vehicleInfo =
                              [vehicle.description, vehicle.short_description]
                                .filter(Boolean)
                                .join(" , ") || "-";
                          }

                          return (
                            <TableRow
                              key={vehicle.key}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>{vehicle.vin || "-"}</TableCell>
                              <TableCell>{vehicleInfo}</TableCell>
                              <TableCell align="center">
                                {/* <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => {
                                    // Determine vehicle type and set form accordingly
                                    const isAutoVehicle =
                                      vehicle.year ||
                                      vehicle.make ||
                                      vehicle.model ||
                                      vehicle.vin;
                                    const isFleetVehicle =
                                      vehicle.description ||
                                      vehicle.short_description;

                                    // Set vehicle information type based on vehicle data
                                    if (isAutoVehicle) {
                                      setFieldValue(
                                        "vehicle_information_type",
                                        "Auto"
                                      );
                                    } else if (isFleetVehicle) {
                                      setFieldValue(
                                        "vehicle_information_type",
                                        "Fleet"
                                      );
                                    }

                                    // Populate form with vehicle data for editing
                                    setCurrentVehicle({
                                      year: vehicle.year?.toString() || "",
                                      make: vehicle.make || "",
                                      model: vehicle.model || "",
                                      vin: vehicle.vin || "",
                                      description: vehicle.description || "",
                                      short_description:
                                        vehicle.short_description || "",
                                    });
                                    setEditingVehicleKey(vehicle.key);
                                  }}
                                  disabled={
                                    editingVehicleKey !== null &&
                                    editingVehicleKey !== vehicle.key
                                  }
                                >
                                  <Edit />
                                </IconButton> */}
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => {
                                    const updatedVehicles =
                                      values.vehicle.filter(
                                        (v) => v.key !== vehicle.key
                                      );
                                    setFieldValue("vehicle", updatedVehicles);
                                    // If deleting the vehicle being edited, reset edit state
                                    if (editingVehicleKey === vehicle.key) {
                                      setEditingVehicleKey(null);
                                      setCurrentVehicle({
                                        year: "",
                                        make: "",
                                        model: "",
                                        vin: "",
                                        description: "",
                                        short_description: "",
                                      });
                                    }
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </Grid>
            <LabelCard heading="Email Message" />
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <CustomFormLabel>Salutation</CustomFormLabel>
                <CustomTextField
                  name="email_salutations"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.email_salutations}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomFormLabel>Customized Message</CustomFormLabel>
                <CustomTextField
                  name="email_customized_message"
                  variant="outlined"
                  size="small"
                  type="text"
                  multiline
                  rows={4}
                  fullWidth
                  value={values.email_customized_message}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
              <RoleBasedCustomButton
                sx={{ width: "15%" }}
                disabled={saveLoading}
                variant="contained"
                moduleKey={MODULE_IDS.CSIO_EDOCS}
                type="submit"
              >
                Save
              </RoleBasedCustomButton>
            </Box>
          </form>
        </StandadCard>
      </PageLoader>
    </PageContainer>
  );
};
